import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output, signal, ViewChild } from '@angular/core';
import { MaintenanceOrderStatus } from '../../../models/maintenance-order-status.model';
import { Mode } from '../../../models/mode.model';
import { Shop } from '../../../models/shop.model';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { ReplaySubject, takeUntil, Observable, map, timer, firstValueFrom, catchError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthPolicies } from '../../../auth/auth-policies';
import { ApprovalFilterForm, WorkOrderFilterForm } from '../../../form-model/work-order-filter.form.model';
import { WorkOrderSearchTextForm } from '../../../form-model/work-order-search-text.form.model';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { BulkWorkOrderCompletionBody } from '../../../models/bulk-work-order/bulk-work-order-completion.model';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { BulkWorkOrderService } from '../../../services/bulk-work-order.service';
import { MasterDataService } from '../../../services/master-data.service';
import { NotificationService } from '../../../services/notification.service';
import { ToastrService } from '../../../services/toastr-service';
import { WorkOrderService } from '../../../services/work-order.service';
import { ApprovalAllAdvanceFilterComponent } from '../approval-all-advance-filter/approval-all-advance-filter.component';
import { RepairMode } from '../../../models/loc.model';
import { ApprovalWoService } from '../../../services/approval-wo.service';
type FilterValue = string | string[] | boolean | Mode[] | MaintenanceOrderStatus[] | Shop[] | RepairMode[];
import * as XLSX from 'xlsx';
import { ManualApprovalHeader } from '../../../models/manual-approval-header.model';
@Component({
  selector: 'app-approval-all-filters',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, ApprovalAllAdvanceFilterComponent, TagWithOverflowComponent],
  templateUrl: './approval-all-filters.component.html',
  styleUrl: './approval-all-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ApprovalAllFiltersComponent implements OnInit, AfterViewInit {
  bulkWorkOrderEnabled = signal(environment.Features.EnableBulkWorkOrder);
  showAdvanceFilters: boolean = false;
  canUserCreateWO: boolean = false;
  taggedStatusList: MaintenanceOrderStatus[] = [];
  untaggedStatusList: MaintenanceOrderStatus[] = [];
  readonly screenWidth = window.innerWidth;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    workOrderFilterForm: FormGroup<ApprovalFilterForm>;
    workOrderSearchTextForm: FormGroup<WorkOrderSearchTextForm>;
  }>;
  @Input() selectedRows: BulkWorkOrderCompletionBody[] = [];
  savedFilter!: FormGroup<ApprovalFilterForm>;
  taggedFilters: { key: string; value: FilterValue }[] = [];
  markCompleteApiCallStatus: APICallStatus = 'NOT_STARTED';
  @Output() markAsCompleteSuccess = new EventEmitter<boolean>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() isWorkOrderList: boolean = true;
  advancedFilterLoading = false;
  downloadData!: ManualApprovalHeader[] | null;
  isDownload: boolean = false;
  @ViewChild(ApprovalAllAdvanceFilterComponent) advFilter!: ApprovalAllAdvanceFilterComponent;
  constructor(
    public authPolicies: AuthPolicies,
    private masterDataService: MasterDataService,
    private formBuilder: FormBuilder,
    private approvalWOService: ApprovalWoService,
    private toastrService: ToastrService,
    private appTextSvc: ApplicationtTextService,
  ) {}
  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }

  ngOnInit() {
    this.masterDataService
      .getMaintenanceOrderStatus()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((statuscount) => {
        this.untaggedStatusList = statuscount;
      });
    if (this.authPolicies.canCreateWorkOrder) {
      this.canUserCreateWO = true;
    }
  }
  private subscribeToFormChanges() {
    this.filterFormGroup.controls.workOrderFilterForm?.controls.applyWOFilter?.valueChanges.subscribe((value) => {
      if (value) {
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
      }
    });
    this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter?.valueChanges.subscribe((_) => {
      this.updateSelectedFiltersSection();
    });
  }
  get isRowSelectedLimitExceeded(): boolean {
    return this.selectedRows.length > 50;
  }
  get isAnyRowNotApproved(): boolean {
    return this.selectedRows.some((row) => row.statusCode !== 390);
  }
  get isMarkAsCompleteValid(): boolean {
    return this.isAnyRowNotApproved || this.isRowSelectedLimitExceeded;
  }

  selectStatusFromQuickFilter(statusCount: MaintenanceOrderStatus): void {
    const newStatus: MaintenanceOrderStatus = {
      generatedCode: statusCount.generatedCode,
      description: statusCount.description,
    };

    const currentStatuses = this.filterFormGroup.controls.workOrderFilterForm.controls.status.value || [];
    if (!currentStatuses.some((status) => status.generatedCode === newStatus.generatedCode)) {
      const updatedStatuses = [...currentStatuses, newStatus];
      this.filterFormGroup.controls.workOrderFilterForm.controls.status.setValue(updatedStatuses);
      this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
      this.updateSelectedFiltersSection();
    }
  }

  unselectWorkorderNumber(): (_: string) => void {
    return (workorderNumber: string) => {
      const workorderNumberControl = this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber;
      const currentValues = this.taggedFilters.find((x) => x.key == 'WO Number')?.value as string[];
      const updatedValue = currentValues.filter((wonumber) => wonumber != workorderNumber);
      const key = this.taggedFilters.find((x) => x.key == 'WO Number');
      if (key) {
        key.value = updatedValue;
      }
      workorderNumberControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectEquipmentNumber(): (_: string) => void {
    return (equipmentNumber: string) => {
      let key = this.taggedFilters.find((x) => x.key == 'Equipment Id');
      const equipmentNumberControl = this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId;
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((eqnumber) => eqnumber != equipmentNumber);
      if (key) {
        key.value = updatedValue;
      }
      equipmentNumberControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }
  unselectSpecificFilter(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1, itemValue = ''): void {
    switch (taggedFilter.key) {
      case 'Equipment Id':
        this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.setValue(null);
        break;
      case 'WO Number':
        const woNumbers = this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value as string[];
        const updatedWoNumbers = woNumbers.filter((woNumber) => woNumber !== itemValue);
        this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.setValue(updatedWoNumbers.length ? updatedWoNumbers : null);
        break;
        break;

      case 'Status':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let statuses = taggedFilter.value as MaintenanceOrderStatus[];
          const updatedStatuses: MaintenanceOrderStatus[] = statuses.filter(
            (_: MaintenanceOrderStatus, index: number) => index !== itemNumber,
          );
          this.filterFormGroup.controls.workOrderFilterForm.controls.status.setValue(updatedStatuses.length ? updatedStatuses : null);
        }
        break;
      case 'Mode':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let statuses = taggedFilter.value as RepairMode[];
          this.filterFormGroup.controls.workOrderFilterForm.controls.status.setValue(null);
        }
        break;

      case 'From':
        this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.setValue(null);
        break;

      case 'To':
        this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.setValue(null);
        break;

      case 'Region':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let region = taggedFilter.value as string[];
          const updatedRegion: string[] = region.filter((_: string, index: number) => index !== itemNumber);
          this.filterFormGroup.controls.workOrderFilterForm.controls.region.setValue(updatedRegion.length ? updatedRegion : null);
        }
        break;

      case 'Cluster':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let cluster = taggedFilter.value as string[];
          const updatedCluster: string[] = cluster.filter((_: string, index: number) => index !== itemNumber);
          this.filterFormGroup.controls.workOrderFilterForm.controls.cluster.setValue(updatedCluster.length ? updatedCluster : null);
        }
        break;
      case 'Shop Country':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let shopCountry = taggedFilter.value as string[];
          const updatedShopCountry: string[] = shopCountry.filter((_: string, index: number) => index !== itemNumber);
          this.filterFormGroup.controls.workOrderFilterForm.controls.shopCountry.setValue(
            updatedShopCountry.length ? updatedShopCountry : null,
          );
        }
        break;

      case 'Shop ID':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let shopId = taggedFilter.value as string[];
          const updatedShopId: string[] = shopId.filter((_: string, index: number) => index !== itemNumber);
          this.filterFormGroup.controls.workOrderFilterForm.controls.shop.setValue(updatedShopId.length ? updatedShopId : null);
        }
        break;

      case 'Assigned To':
        this.filterFormGroup.controls.workOrderFilterForm.controls.assignedTo.setValue(null);
        break;
      case 'Show only hold work orders':
        this.filterFormGroup.controls.workOrderFilterForm.controls.isOnHold.setValue(null);
        break;
      default:
        break;
    }
    this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  showAdvanceFiltersModal() {
    this.showAdvanceFilters = true;
    this.saveCurrentFilters();
  }
  advancedFilterHasOpened() {
    this.advancedFilterLoading = false;
  }
  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.controls.workOrderFilterForm.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters() {
    this.advFilter.clearFilters();
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  private saveCurrentFilters(): void {
    this.savedFilter = this.formBuilder.group({
      equipmentId: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.value),
      mode: this.formBuilder.control<RepairMode[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value),
      woNumber: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value),
      status: this.formBuilder.control<MaintenanceOrderStatus[] | null>(
        this.filterFormGroup.controls.workOrderFilterForm.controls.status.value,
      ),
      fromDate: this.formBuilder.control<string | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.value),
      toDate: this.formBuilder.control<string | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.value),
      region: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.region.value),
      cluster: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.cluster.value),
      shopCountry: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.shopCountry.value),
      shop: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value),
      isOnHold: this.formBuilder.control<boolean | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.isOnHold.value),
      applyWOFilter: this.formBuilder.control<boolean | null>(
        this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.value,
      ),
      assignedTo: this.formBuilder.control<string | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.assignedTo.value),
    });
  }

  updateSelectedFiltersSection() {
    this.markAsTagged('Equipment Id', this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.value);
    this.markAsTagged('Mode', this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value);
    this.markAsTagged('WO Number', this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value);
    this.markAsTagged('From', this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.value);
    this.markAsTagged('To', this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.value);
    this.markAsTagged('Region', this.filterFormGroup.controls.workOrderFilterForm.controls.region.value);
    this.markAsTagged('Cluster', this.filterFormGroup.controls.workOrderFilterForm.controls.cluster.value);
    this.markAsTagged('Shop Country', this.filterFormGroup.controls.workOrderFilterForm.controls.shopCountry.value);
    this.markAsTagged('Shop ID', this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value);
    this.markAsTagged('Assigned To', this.filterFormGroup.controls.workOrderFilterForm.controls.assignedTo.value);
    this.markAsTagged('Show only hold work orders', this.filterFormGroup.controls.workOrderFilterForm.controls.isOnHold.value);
    this.updateStatusTag();
  }

  private updateStatusTag() {
    this.masterDataService
      .getMaintenanceOrderStatus()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const selectedStatuses = this.filterFormGroup.controls.workOrderFilterForm.controls.status.value || [];

          this.taggedStatusList = response.filter((status) =>
            selectedStatuses.some((selected) => selected.generatedCode === status.generatedCode),
          );
          this.untaggedStatusList = response.filter(
            (status) => !selectedStatuses.some((selected) => selected.generatedCode === status.generatedCode),
          );
          this.markAsTagged('Status', this.filterFormGroup.controls.workOrderFilterForm.controls.status.value);
        },
        error: (err) => {},
      });
  }

  markAsTagged(key: string, newValue: FilterValue | null) {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string) {
    this.taggedFilters = this.taggedFilters.filter((filter) => filter.key !== key);
  }

  getFilterValue(key: string) {
    const filter = this.taggedFilters.find((filter) => filter.key === key);
    return filter ? filter.value : null;
  }

  getTaggedFilterValue(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1): FilterValue {
    switch (taggedFilter.key) {
      case 'From':
      case 'To':
      case 'Assigned To':
        return taggedFilter.value;

      case 'Region':
      case 'Cluster':
      case 'Shop Country':
      case 'Shop ID':
        let region = taggedFilter.value as string[];
        return region[itemNumber];
      case 'Status':
        let statuses = taggedFilter.value as MaintenanceOrderStatus[];
        return `${statuses[itemNumber].description}`;
      case 'Mode':
        let modes = taggedFilter.value as RepairMode[];
        return `${modes[itemNumber].repairMode}`;
      case 'Show only hold work orders':
        let isOnHold = taggedFilter.value as boolean;
        return isOnHold ? 'Yes' : 'No';
      default:
        return '';
    }
  }

  getTaggedWorkOrderNumbers(): string[] {
    return (this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'WO Number')?.value as string[]) || [];
  }
  getTaggedEquipmentNumbers(): string[] {
    return (this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Equipment Id')?.value as string[]) || [];
  }
  getTaggedStatuses(): MaintenanceOrderStatus[] {
    const statusTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Status');
    return statusTag && Array.isArray(statusTag.value) ? (statusTag.value as MaintenanceOrderStatus[]) : [];
  }
  getTaggedModes(): RepairMode[] {
    const statusTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Mode');
    return statusTag && Array.isArray(statusTag.value) ? (statusTag.value as RepairMode[]) : [];
  }
  getTaggedRegions(): string[] {
    const statusTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Region');
    return statusTag && Array.isArray(statusTag.value) ? (statusTag.value as string[]) : [];
  }
  getTaggedClusters(): string[] {
    const statusTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Cluster');
    return statusTag && Array.isArray(statusTag.value) ? (statusTag.value as string[]) : [];
  }
  getTaggedShopCountries(): string[] {
    const statusTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Shop Country');
    return statusTag && Array.isArray(statusTag.value) ? (statusTag.value as string[]) : [];
  }
  getTaggedShopID(): string[] {
    const statusTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Shop ID');
    return statusTag && Array.isArray(statusTag.value) ? (statusTag.value as string[]) : [];
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  get numberOfQuickFiltersToShow(): number {
    if (this.screenWidth >= 320 && this.screenWidth < 768) {
      return 2;
    }
    if (this.screenWidth >= 768 && this.screenWidth < 1024) {
      return 3;
    }
    if (this.screenWidth >= 1024 && this.screenWidth < 1280) {
      return 4;
    }
    if (this.screenWidth >= 1280 && this.screenWidth < 1920) {
      return 6;
    }
    if (this.screenWidth >= 1920 && this.screenWidth < 2560) {
      return 8;
    }
    if (this.screenWidth >= 2560 && this.screenWidth < 3840) {
      return 12;
    }
    return 100;
  }

  onRefresh() {
    this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
  }

  onDownload() {
    this.isDownload = true;

    if (Object.keys(this.approvalWOService.filterData).length > 0) {
      this.approvalWOService.filterData.isDownloadRequest = true;
      this.approvalWOService
        .onDownload(this.approvalWOService.filterData)
        .pipe(
          takeUntil(this.destroyed$),
          catchError((err) => {
            this.isDownload = false;
            this.toastrService.showToastr(this.appTextSvc.unableToDownload, 'error');
            throw err;
          }),
        )
        .subscribe((x) => {
          this.isDownload = false;
          this.downloadData = x.body;
          if (this.downloadData) {
            const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.downloadData);
            const wb: XLSX.WorkBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}_${hours}-${minutes}`;
            const fileName = `Approval-All-WO_${formattedDate}.xlsx`;
            XLSX.writeFile(wb, fileName);
          }
        });
    } else {
      this.isDownload = false;
      this.toastrService.showToastr(this.appTextSvc.noDataDownload, 'error');
    }
  }
}
